import { getToken } from '@luminovo/auth';
import { assertPresent } from '@luminovo/commons';
import type { AppRouter } from '@luminovo/gateway-contracts';
import { useQueryClient } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import { createTRPCContext } from '@trpc/tanstack-react-query';
import React from 'react';
import { deserialize, serialize } from 'superjson';

const { TRPCProvider, useTRPC, useTRPCClient } = createTRPCContext<AppRouter>();
export const useTrpc = useTRPC;
export const useTrpcClient = useTRPCClient;

export const TrpcProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
    const queryClient = useQueryClient();
    const [trpcClient] = React.useState(() =>
        createTRPCClient<AppRouter>({
            links: [
                httpBatchLink({
                    transformer: {
                        serialize,
                        deserialize,
                    },
                    url: getTrpcUrl(),
                    headers() {
                        return {
                            Authorization: `Bearer ${getToken()}`,
                        };
                    },
                }),
            ],
        }),
    );
    return (
        <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
            <MonitoringQueryDefaults>{children}</MonitoringQueryDefaults>
        </TRPCProvider>
    );
};

function MonitoringQueryDefaults({ children }: { children: React.ReactNode }): JSX.Element {
    const queryClient = useQueryClient();
    const trpc = useTrpc();

    // Register before children start queries. The router path matches all monitoring
    // queries (including infinite queries), but not mutations or other routers.
    queryClient.setQueryDefaults(trpc.monitoring.pathKey(), { meta: { globalErrorHandler: false } });

    return <>{children}</>;
}

function getTrpcUrl() {
    return assertPresent(import.meta.env.TRPC_URL);
}
